import React, { useState, useEffect } from 'react';
import MainLayout from '../components/MainLayout';
import axios from 'axios';
import { FileCheck, HelpCircle, AlertCircle, Plus, X, Send, MessageSquare, FileText, Download } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const ESSDocuments = () => {
  const [activeTab, setActiveTab] = useState('documents');
  const [documents, setDocuments] = useState([]);
  const [taxSheets, setTaxSheets] = useState([]);
  const [taxSheetsLoading, setTaxSheetsLoading] = useState(true);
  const [taxSheetsError, setTaxSheetsError] = useState(null);
  const [tickets, setTickets] = useState([]);
  const [fnf, setFnf] = useState(null);
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [showNewTicket, setShowNewTicket] = useState(false);
  const [ticketForm, setTicketForm] = useState({ subject: '', category: 'general', description: '', priority: 'medium' });
  const [replyMsg, setReplyMsg] = useState('');

  useEffect(() => { fetchAll(); }, []);

  const fetchAll = async () => {
    try {
      setTaxSheetsLoading(true);
      setTaxSheetsError(null);
      const [docs, tsheets, tix, fnfData] = await Promise.all([
        axios.get(`${API_URL}/ess/documents`, { withCredentials: true }),
        axios.get(`${API_URL}/ess/tax-sheets`, { withCredentials: true })
          .catch(err => {
            setTaxSheetsError(err?.response?.data?.detail || 'Failed to load Tax Sheets');
            return { data: [] };
          }),
        axios.get(`${API_URL}/ess/helpdesk`, { withCredentials: true }),
        axios.get(`${API_URL}/ess/fnf`, { withCredentials: true }),
      ]);
      setDocuments(docs.data);
      setTaxSheets(tsheets.data || []);
      setTickets(tix.data);
      setFnf(fnfData.data);
    } catch (e) { console.error(e); } finally {
      setTaxSheetsLoading(false);
    }
  };

  const handleCreateTicket = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API_URL}/ess/helpdesk`, ticketForm, { withCredentials: true });
      setShowNewTicket(false);
      setTicketForm({ subject: '', category: 'general', description: '', priority: 'medium' });
      fetchAll();
    } catch (e) { alert(e.response?.data?.detail || 'Error'); }
  };

  const handleReply = async () => {
    if (!replyMsg.trim() || !selectedTicket) return;
    try {
      await axios.post(`${API_URL}/ess/helpdesk/${selectedTicket.id}/respond`, { message: replyMsg }, { withCredentials: true });
      setReplyMsg('');
      const { data } = await axios.get(`${API_URL}/ess/helpdesk/${selectedTicket.id}`, { withCredentials: true });
      setSelectedTicket(data);
      fetchAll();
    } catch (e) { alert(e.response?.data?.detail || 'Error'); }
  };

  const statusColor = (s) => ({ open: 'bg-blue-100 text-blue-700', in_progress: 'bg-amber-100 text-amber-700', closed: 'bg-emerald-100 text-emerald-700', resolved: 'bg-emerald-100 text-emerald-700' }[s] || 'bg-slate-100 text-slate-600');
  const prioColor = (p) => ({ urgent: 'bg-red-100 text-red-700', high: 'bg-orange-100 text-orange-700', medium: 'bg-amber-100 text-amber-700', low: 'bg-slate-100 text-slate-600' }[p] || 'bg-slate-100 text-slate-600');

  const tabs = [
    { key: 'documents', label: 'Documents', icon: FileCheck },
    { key: 'tax_sheets', label: 'Tax Sheets', icon: FileText },
    { key: 'helpdesk', label: 'Helpdesk', icon: HelpCircle },
    { key: 'fnf', label: 'Final Settlement', icon: AlertCircle },
  ];

  return (
    <MainLayout>
      <div data-testid="ess-documents-page" className="space-y-6 max-w-5xl">
        <h1 className="text-2xl font-bold text-slate-900">Documents & Support</h1>

        <div className="flex space-x-1 bg-slate-100 rounded-lg p-1 overflow-x-auto">
          {tabs.map((tab) => (
            <button key={tab.key} data-testid={`tab-${tab.key}`} onClick={() => setActiveTab(tab.key)}
              className={`flex items-center space-x-2 py-2 px-4 rounded-md text-sm font-medium whitespace-nowrap transition-colors ${activeTab === tab.key ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-500 hover:text-slate-700'}`}>
              <tab.icon className="w-4 h-4" /><span>{tab.label}</span>
            </button>
          ))}
        </div>

        {/* DOCUMENTS TAB */}
        {activeTab === 'documents' && (
          <div className="bg-white rounded-xl border border-slate-200 divide-y divide-slate-100">
            {documents.length > 0 ? documents.map((d, i) => (
              <div key={i} data-testid={`doc-${i}`} className="flex items-center justify-between p-4 hover:bg-slate-50">
                <div className="flex items-center space-x-3">
                  <div className="p-2 bg-indigo-50 rounded-lg"><FileCheck className="w-5 h-5 text-indigo-600" /></div>
                  <div><p className="text-sm font-medium text-slate-800">{d.document_type || d.name || 'Document'}</p><p className="text-xs text-slate-500">{d.uploaded_at || d.issue_date || ''}</p></div>
                </div>
              </div>
            )) : (
              <div className="p-8 text-center">
                <FileCheck className="w-10 h-10 text-slate-300 mx-auto mb-3" />
                <p className="text-sm text-slate-400">No documents available</p>
              </div>
            )}
          </div>
        )}

        {/* TAX SHEETS TAB */}
        {activeTab === 'tax_sheets' && (
          <div className="bg-white rounded-xl border border-slate-200 divide-y divide-slate-100 overflow-hidden" data-testid="ess-tax-sheets-tab">
            {taxSheetsLoading ? (
              <div className="p-10 text-center space-y-2">
                <div className="w-8 h-8 border-4 border-blue-200 border-t-blue-600 rounded-full animate-spin mx-auto mb-2" />
                <p className="text-sm text-slate-500">Loading Tax Sheets…</p>
              </div>
            ) : taxSheetsError ? (
              <div className="p-10 text-center space-y-2">
                <FileText className="w-10 h-10 text-rose-300 mx-auto mb-2" />
                <p className="text-sm font-semibold text-rose-700">Could not load Tax Sheets</p>
                <p className="text-xs text-slate-400 max-w-md mx-auto">{taxSheetsError}</p>
                <button onClick={fetchAll} className="mt-3 px-4 py-1.5 bg-[#0052FF] hover:bg-[#0040CC] text-white text-xs font-semibold rounded-lg transition-colors">Retry</button>
              </div>
            ) : taxSheets.length > 0 ? taxSheets.map((ts, i) => (
              <div key={ts.id || i} data-testid={`ess-ts-item-${i}`} className="flex items-center justify-between p-4 hover:bg-slate-50/80 transition-colors">
                <div className="flex items-center space-x-3">
                  <div className="p-2.5 bg-blue-50 text-[#0052FF] rounded-xl border border-blue-100">
                    <FileText className="w-5 h-5" />
                  </div>
                  <div>
                    <div className="flex items-center gap-2">
                      <p className="text-sm font-semibold text-slate-900 font-mono">{ts.payroll_period}</p>
                      <span className={`px-2 py-0.5 rounded text-[10px] font-bold uppercase tracking-wider ${
                        ts.payroll_type === 'legacy' ? 'bg-purple-100 text-purple-800' : 'bg-blue-100 text-blue-800'
                      }`}>
                        {ts.payroll_type || 'regular'}
                      </span>
                    </div>
                    <p className="text-xs text-slate-500 mt-0.5">
                      FY {ts.financial_year} · Taxable Income: ₹{(ts.taxable_income || 0).toLocaleString()} · Tax Deducted: ₹{(ts.tax_deducted || 0).toLocaleString()}
                    </p>
                    {ts.released_at && (
                      <p className="text-xs text-slate-400 mt-0.5">Released: {new Date(ts.released_at).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })}</p>
                    )}
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <span className="inline-flex items-center gap-1 text-[11px] font-semibold text-emerald-700 bg-emerald-50 px-2.5 py-1 rounded-md border border-emerald-200">
                    <span className="w-1.5 h-1.5 rounded-full bg-emerald-600 animate-pulse"></span> Released
                  </span>
                  <a
                    href={`${API_URL}/ess/tax-sheets/${ts.id}/pdf`}
                    target="_blank"
                    rel="noreferrer"
                    data-testid={`ess-ts-download-${ts.id}`}
                    className="flex items-center gap-1.5 px-3 py-1.5 bg-[#0052FF] hover:bg-[#0040CC] text-white rounded-lg text-xs font-semibold shadow-sm transition-colors"
                  >
                    <Download className="w-3.5 h-3.5" /> View / Download PDF
                  </a>
                </div>
              </div>
            )) : (
              <div className="p-10 text-center space-y-2">
                <FileText className="w-10 h-10 text-slate-300 mx-auto mb-2" />
                <p className="text-sm font-semibold text-slate-700">No released Tax Sheets available yet</p>
                <p className="text-xs text-slate-400 max-w-md mx-auto">
                  Once your HR/Payroll team releases your Tax Computation Sheet, it will appear here permanently.
                </p>
              </div>
            )}
          </div>
        )}

        {/* HELPDESK TAB */}
        {activeTab === 'helpdesk' && (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            <div className="lg:col-span-1 space-y-3">
              <div className="flex items-center justify-between">
                <h2 className="text-sm font-semibold text-slate-700">My Tickets</h2>
                <button data-testid="new-ticket-btn" onClick={() => setShowNewTicket(true)} className="flex items-center space-x-1 bg-[#0052FF] text-white px-3 py-1.5 rounded-lg text-xs font-medium hover:bg-[#0040CC]"><Plus className="w-3 h-3" /><span>New Ticket</span></button>
              </div>
              {tickets.map((t) => (
                <div key={t.id} data-testid={`ticket-${t.id}`} onClick={() => setSelectedTicket(t)}
                  className={`bg-white rounded-xl border p-3 cursor-pointer transition-colors ${selectedTicket?.id === t.id ? 'border-[#0052FF] bg-blue-50/30' : 'border-slate-200 hover:border-slate-300'}`}>
                  <div className="flex items-center justify-between mb-1">
                    <span className={`px-1.5 py-0.5 rounded text-xs font-medium ${statusColor(t.status)}`}>{t.status}</span>
                    <span className={`px-1.5 py-0.5 rounded text-xs font-medium ${prioColor(t.priority)}`}>{t.priority}</span>
                  </div>
                  <p className="text-sm font-medium text-slate-800 line-clamp-1">{t.subject}</p>
                  <p className="text-xs text-slate-400 mt-0.5">{t.category}</p>
                </div>
              ))}
              {tickets.length === 0 && <p className="text-sm text-slate-400 text-center py-6">No tickets</p>}
            </div>

            <div className="lg:col-span-2">
              {selectedTicket ? (
                <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
                  <div className="p-4 border-b border-slate-100">
                    <div className="flex items-center justify-between">
                      <h3 className="text-base font-semibold text-slate-800">{selectedTicket.subject}</h3>
                      <div className="flex items-center space-x-2">
                        <span className={`px-2 py-0.5 rounded text-xs font-medium ${statusColor(selectedTicket.status)}`}>{selectedTicket.status}</span>
                        <span className={`px-2 py-0.5 rounded text-xs font-medium ${prioColor(selectedTicket.priority)}`}>{selectedTicket.priority}</span>
                      </div>
                    </div>
                    <p className="text-xs text-slate-500 mt-1">Category: {selectedTicket.category} &middot; Created: {new Date(selectedTicket.created_at).toLocaleString()}</p>
                  </div>
                  <div className="p-4 bg-slate-50/50 border-b border-slate-100">
                    <p className="text-sm text-slate-700">{selectedTicket.description}</p>
                  </div>
                  <div className="p-4 space-y-3 max-h-60 overflow-y-auto">
                    {selectedTicket.responses?.map((r, i) => (
                      <div key={i} className="bg-slate-50 rounded-lg p-3">
                        <div className="flex items-center justify-between mb-1"><span className="text-xs font-medium text-slate-700">{r.by_name || 'User'}</span><span className="text-xs text-slate-400">{new Date(r.at).toLocaleString()}</span></div>
                        <p className="text-sm text-slate-600">{r.message}</p>
                      </div>
                    ))}
                  </div>
                  <div className="p-4 border-t border-slate-100 flex space-x-2">
                    <input data-testid="ticket-reply-input" value={replyMsg} onChange={(e) => setReplyMsg(e.target.value)} placeholder="Type a reply..." className="flex-1 border border-slate-300 rounded-lg px-3 py-2 text-sm" onKeyDown={(e) => e.key === 'Enter' && handleReply()} />
                    <button data-testid="ticket-reply-btn" onClick={handleReply} className="bg-[#0052FF] text-white p-2 rounded-lg hover:bg-[#0040CC]"><Send className="w-4 h-4" /></button>
                  </div>
                </div>
              ) : (
                <div className="bg-white rounded-xl border border-slate-200 p-12 text-center">
                  <MessageSquare className="w-10 h-10 text-slate-300 mx-auto mb-3" />
                  <p className="text-sm text-slate-400">Select a ticket to view conversation</p>
                </div>
              )}
            </div>
          </div>
        )}

        {/* FNF TAB */}
        {activeTab === 'fnf' && (
          <div className="bg-white rounded-xl border border-slate-200 p-6 space-y-6">
            {fnf ? (
              <div className="space-y-6">
                <div className="flex items-center justify-between flex-wrap gap-4 pb-4 border-b border-slate-100">
                  <div>
                    <h3 className="text-lg font-bold text-slate-900 flex items-center gap-2">
                      Full &amp; Final Settlement Statement
                      {fnf.settlement_number && <span className="bg-blue-50 text-blue-700 text-xs font-mono px-2 py-0.5 rounded border border-blue-200">{fnf.settlement_number}</span>}
                    </h3>
                    <p className="text-xs text-slate-500">Last Working Day: {fnf.last_working_date || fnf.last_working_day || '—'}</p>
                  </div>
                  <div className="flex items-center gap-3">
                    <span className={`px-3 py-1 rounded-full text-xs font-semibold uppercase tracking-wider ${
                      fnf.status === 'paid' ? 'bg-emerald-100 text-emerald-800 border border-emerald-300' :
                      fnf.status === 'ready_for_payroll' ? 'bg-blue-100 text-blue-800 border border-blue-300' :
                      'bg-slate-100 text-slate-700'
                    }`}>
                      {fnf.status === 'ready_for_payroll' ? 'Ready for Payroll' : (fnf.status || 'Draft')}
                    </span>
                    {(fnf.id || fnf._id) && (
                      <a
                        href={`${API_URL}/payroll/fnf/${fnf.id || fnf._id}/pdf`}
                        target="_blank"
                        rel="noreferrer"
                        className="flex items-center gap-1.5 bg-[#0052FF] hover:bg-[#0040CC] text-white px-3 py-1.5 rounded-lg text-xs font-semibold transition-colors"
                      >
                        Download PDF Statement
                      </a>
                    )}
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm bg-slate-50 p-4 rounded-xl border border-slate-200">
                  <div>
                    <span className="text-xs text-slate-500 block">Gross Settlement</span>
                    <span className="text-base font-semibold text-slate-900">₹ {(fnf.gross_settlement || fnf.total_earnings || 0).toLocaleString('en-IN')}</span>
                  </div>
                  <div>
                    <span className="text-xs text-slate-500 block">Total Recoveries & Deductions</span>
                    <span className="text-base font-semibold text-rose-600">₹ {(fnf.total_deductions || 0).toLocaleString('en-IN')}</span>
                  </div>
                  <div>
                    <span className="text-xs text-slate-500 block">Net Payable Amount</span>
                    <span className="text-lg font-bold text-emerald-600">₹ {(fnf.net_settlement || fnf.total_amount || 0).toLocaleString('en-IN')}</span>
                  </div>
                </div>

                {fnf.wage_period_config && (
                  <div className="bg-slate-50 border border-slate-200 rounded-lg p-3 text-xs space-y-1">
                    <div className="flex justify-between font-medium text-slate-800">
                      <span>Company Configured Wage Cycle: <strong className="text-blue-700">{fnf.wage_period_config.label}</strong></span>
                      {fnf.wage_periods_breakdown && fnf.wage_periods_breakdown.length > 0 && (
                        <span>Calculated Wage Periods: <strong>{fnf.wage_periods_breakdown.length} Period(s)</strong></span>
                      )}
                    </div>
                  </div>
                )}

                {fnf.payroll_month && (
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 text-xs text-blue-800 flex items-center justify-between">
                    <span>
                      <strong>Disbursement Schedule:</strong> Included for payment in <strong>{fnf.payroll_month}</strong> Payroll Run.
                    </span>
                    {fnf.paid_at && <span>Paid on: {new Date(fnf.paid_at).toLocaleDateString()}</span>}
                  </div>
                )}

                {/* Earnings & Deductions Breakdown */}
                {((fnf.earnings_breakdown && fnf.earnings_breakdown.length > 0) || (fnf.components && fnf.components.length > 0)) && (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="border border-slate-200 rounded-lg p-4">
                      <h4 className="text-xs font-bold text-emerald-800 uppercase tracking-wider mb-3">Earnings & Payouts</h4>
                      <div className="space-y-2">
                        {(fnf.earnings_breakdown || fnf.components.filter(c => c.type === 'earning' || c.category === 'earning')).map((item, idx) => (
                          <div key={idx} className="flex justify-between py-1.5 border-b border-slate-100 text-xs">
                            <span className="text-slate-700 font-medium">{item.label || item.name}</span>
                            <span className="font-semibold text-emerald-600">₹ {(Number(item.amount) || 0).toLocaleString('en-IN')}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                    <div className="border border-slate-200 rounded-lg p-4">
                      <h4 className="text-xs font-bold text-rose-800 uppercase tracking-wider mb-3">Deductions & Clearances</h4>
                      <div className="space-y-2">
                        {(fnf.deductions_breakdown || fnf.components.filter(c => c.type === 'deduction' || c.category === 'deduction')).map((item, idx) => (
                          <div key={idx} className="flex justify-between py-1.5 border-b border-slate-100 text-xs">
                            <span className="text-slate-700 font-medium">{item.label || item.name}</span>
                            <span className="font-semibold text-rose-600">₹ {(Number(item.amount) || 0).toLocaleString('en-IN')}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                )}
              </div>
            ) : (
              <div className="text-center py-10">
                <AlertCircle className="w-10 h-10 text-slate-300 mx-auto mb-3" />
                <p className="text-sm font-semibold text-slate-700">No Full &amp; Final Settlement Record</p>
                <p className="text-xs text-slate-400 mt-1">Once your exit separation is processed by HR &amp; Payroll, your settlement statement will appear here.</p>
              </div>
            )}
          </div>
        )}

        {/* NEW TICKET MODAL */}
        {showNewTicket && (
          <div className="fixed inset-0 bg-black/40 flex items-end md:items-center justify-center z-50" onClick={() => setShowNewTicket(false)}>
            <div className="bg-white rounded-t-2xl md:rounded-xl p-6 w-full max-w-md shadow-xl" onClick={(e) => e.stopPropagation()}>
              <div className="flex items-center justify-between mb-4"><h3 className="text-lg font-semibold text-slate-900">New Helpdesk Ticket</h3><button onClick={() => setShowNewTicket(false)}><X className="w-5 h-5 text-slate-400" /></button></div>
              <form onSubmit={handleCreateTicket} className="space-y-4">
                <div><label className="block text-sm font-medium text-slate-700 mb-1">Subject</label><input data-testid="ticket-subject" value={ticketForm.subject} onChange={(e) => setTicketForm({ ...ticketForm, subject: e.target.value })} className="w-full border border-slate-300 rounded-lg px-3 py-2.5 text-sm" required /></div>
                <div className="grid grid-cols-2 gap-3">
                  <div><label className="block text-sm font-medium text-slate-700 mb-1">Category</label>
                    <select data-testid="ticket-category" value={ticketForm.category} onChange={(e) => setTicketForm({ ...ticketForm, category: e.target.value })} className="w-full border border-slate-300 rounded-lg px-3 py-2.5 text-sm">
                      <option value="general">General</option><option value="payroll">Payroll</option><option value="leave">Leave</option><option value="attendance">Attendance</option><option value="tax">Tax</option><option value="hr">HR</option><option value="it">IT</option>
                    </select></div>
                  <div><label className="block text-sm font-medium text-slate-700 mb-1">Priority</label>
                    <select data-testid="ticket-priority" value={ticketForm.priority} onChange={(e) => setTicketForm({ ...ticketForm, priority: e.target.value })} className="w-full border border-slate-300 rounded-lg px-3 py-2.5 text-sm">
                      <option value="low">Low</option><option value="medium">Medium</option><option value="high">High</option><option value="urgent">Urgent</option>
                    </select></div>
                </div>
                <div><label className="block text-sm font-medium text-slate-700 mb-1">Description</label><textarea data-testid="ticket-desc" value={ticketForm.description} onChange={(e) => setTicketForm({ ...ticketForm, description: e.target.value })} rows={3} className="w-full border border-slate-300 rounded-lg px-3 py-2.5 text-sm" required /></div>
                <button data-testid="submit-ticket-btn" type="submit" className="w-full bg-[#0052FF] text-white py-2.5 rounded-lg text-sm font-medium hover:bg-[#0040CC]">Create Ticket</button>
              </form>
            </div>
          </div>
        )}
      </div>
    </MainLayout>
  );
};

export default ESSDocuments;
